import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import {
  ActionCreators,
  AlarmUtils,
  DateTimeUtils,
  Utils,
  Selectors
} from 'galarm-shared'
import { Box } from 'grommet'
import { colorThemes, Constants, I18n } from 'galarm-config'
import isArray from 'lodash/isArray'
import isPlainObject from 'lodash/isPlainObject'
import CalendarToolbar from './CalendarToolbar'
import debounce from 'lodash/debounce'
import isObjEqual from 'fast-deep-equal'

import 'react-big-calendar/lib/css/react-big-calendar.css'
import './CalendarView.css'

const {
  alarmCategoriesSelector,
  allAlarmsSelector,
  makeAlarmsInCategorySelector,
  calendarEventsSelector,
  instantAlarmsSelector,
  incidentAlarmsSelector,
  taskAlarmsSelector
} = Selectors

const CalendarView = () => {
  const dispatch = useDispatch()
  const webFirstDayOfWeek = useSelector(
    state => state.userSettings.webFirstDayOfWeek
  )
  const currLocale = I18n.locale
  // Override weekdaysShort so react-big-calendar uses the app's translations
  moment.updateLocale(currLocale, {
    week: {
      dow: webFirstDayOfWeek
    },
    weekdaysShort: DateTimeUtils.shortWeekdays
  })
  const localizer = momentLocalizer(moment)

  const lastUsedCalendarView =
    window.localStorage.getItem('calendarView') || 'month'
  const [calendarView, setCalendarView] = useState(lastUsedCalendarView)

  const lastUsedCalendarDate = window.localStorage.getItem('calendarDate')
  const [calendarDate, setCalendarDate] = useState(
    lastUsedCalendarDate ? new Date(parseInt(lastUsedCalendarDate)) : new Date()
  )

  const [monthForCalendarView, setMonthForCalendarView] = useState(
    moment(calendarDate).month()
  )

  const colorScheme = useSelector(state => state.userSettings.webColorScheme)

  const allAlarms = useSelector(allAlarmsSelector)
  const calendarEvents = useSelector(calendarEventsSelector)

  // Filter alarms by chosen alarm category. In addition to the user-defined
  // categories (selected via currentlySelectedAlarmCategoryId), the sidebar
  // also exposes the run-time categories Incidents, Instant Alerts and
  // Calendar Events. These are selected via dedicated appState flags rather
  // than currentlySelectedAlarmCategoryId, so they need to be handled
  // explicitly to filter the calendar.
  const currentlySelectedAlarmCategoryId = useSelector(
    state => state.appState.currentlySelectedAlarmCategoryId
  )
  const showAlertIncidents = useSelector(
    state => state.appState.showAlertIncidents
  )
  const showInstantAlarms = useSelector(
    state => state.appState.showInstantAlarms
  )
  const showTasks = useSelector(state => state.appState.showTasks)
  const showCalendarEvents = useSelector(
    state => state.appState.showCalendarEvents
  )
  const alarmsInCategorySelector = useMemo(
    () => makeAlarmsInCategorySelector(),
    []
  )
  const alarmsInCategory = useSelector(state =>
    alarmsInCategorySelector(state, {
      id: currentlySelectedAlarmCategoryId
    })
  )
  const instantAlarms = useSelector(instantAlarmsSelector)
  const alertIncidents = useSelector(incidentAlarmsSelector)
  const taskAlarms = useSelector(taskAlarmsSelector)

  const alarmsToConsider = useMemo(() => {
    if (showCalendarEvents) {
      return []
    }
    if (showAlertIncidents) {
      return alertIncidents
    }
    if (showInstantAlarms) {
      return instantAlarms
    }
    if (showTasks) {
      return taskAlarms.filter(alarm => alarm.status)
    }

    let alarmsToConsider
    if (currentlySelectedAlarmCategoryId) {
      alarmsToConsider = alarmsInCategory
    } else {
      alarmsToConsider = allAlarms
    }

    alarmsToConsider = alarmsToConsider.filter(alarm => alarm.status)
    return alarmsToConsider
  }, [
    showCalendarEvents,
    showAlertIncidents,
    showInstantAlarms,
    showTasks,
    currentlySelectedAlarmCategoryId,
    alarmsInCategory,
    allAlarms,
    instantAlarms,
    alertIncidents,
    taskAlarms
  ])

  // Calendar events should be plotted alongside alarms only in the default
  // (all alarms) view or when the Calendar Events category is selected. When
  // any other category is selected we show only that category's alarms.
  const includeCalendarEvents =
    showCalendarEvents ||
    (!showAlertIncidents &&
      !showInstantAlarms &&
      !showTasks &&
      !currentlySelectedAlarmCategoryId)

  const [dateRange, setDateRange] = useState({
    start: Date.now(),
    end: Date.now()
  })

  const alarmCategories = useSelector(alarmCategoriesSelector)

  const [events, setEvents] = useState([])

  useEffect(() => {
    const currDateRange = DateTimeUtils.getCalendarMonthRangeForADate(
      calendarDate.getTime()
    )
    setDateRange(currDateRange)
  }, [])

  const calculateEventsForCalendar = () => {
    let events = AlarmUtils.getEventsForAlarmsInRange(
      alarmsToConsider,
      dateRange.start,
      dateRange.end,
      calendarView
    )
    events.forEach(event => {
      const alarmCategory = AlarmUtils.getAlarmCategoryForAlarm(
        alarmCategories,
        event.id
      )
      event.category = alarmCategory
    })

    // Add calendar events
    if (includeCalendarEvents) {
      const calendarEventObjects = AlarmUtils.getEventsForCalendarInRange(
        calendarEvents,
        dateRange.start,
        dateRange.end
      )
      events = events.concat(calendarEventObjects)
    }
    setEvents(events)
  }

  const debouncedCalculateEventsForCalendar = useCallback(
    debounce(calculateEventsForCalendar, 500, {
      leading: true,
      maxWait: 2000
    }),
    [
      alarmsToConsider,
      dateRange,
      calendarView,
      alarmCategories,
      includeCalendarEvents,
      calendarEvents
    ]
  )

  useEffect(() => {
    debouncedCalculateEventsForCalendar()
    return () => debouncedCalculateEventsForCalendar.cancel()
  }, [
    alarmsToConsider,
    calendarEvents,
    dateRange,
    calendarView,
    alarmCategories,
    includeCalendarEvents
  ])

  // const events = allAlarms.map(alarm => {
  //   const alarmDate = AlarmUtils.getCurrentDateForAlarm(alarm)
  //   return {
  //     id: alarm.id,
  //     title: alarm.name,
  //     allDay: false,
  //     start: new Date(alarmDate),
  //     end: new Date(alarmDate)
  //   }
  // })

  const onChangeCalendarView = view => {
    window.localStorage.setItem('calendarView', view)
    setCalendarView(view)
  }

  const onNavigate = date => {
    window.localStorage.setItem('calendarDate', date.getTime())
    setCalendarDate(date)
  }

  const onSelectSlot = ({ start }) => {
    const alarmDate = moment(start).valueOf()
    dispatch(ActionCreators.showSelectAlarmTypeScreen({ alarmDate }))
  }

  // eslint-disable-next-line no-unused-vars
  const onRangeChange = (range, rangeType) => {
    // In case of month range, it is a plain object with start and end
    // Add 1 day to get to the end of the range
    if (isPlainObject(range)) {
      const start = moment(range.start).valueOf()
      const end = moment(range.end).add(1, 'day').valueOf()
      const newDateRange = { start, end }
      if (!isObjEqual(newDateRange, dateRange)) {
        setDateRange(newDateRange)
      }

      if (moment(start).date() === 1) {
        setMonthForCalendarView(moment(start).month())
      } else {
        setMonthForCalendarView(moment(start).month() + 1)
      }
    }
    // In case of week and day, it is an array containing the days
    // Add 1 day to get to the end of the range
    else if (isArray(range)) {
      const start = moment(range[0]).valueOf()
      const end = moment(range[range.length - 1])
        .add(1, 'day')
        .valueOf()
      const newDateRange = { start, end }
      if (!isObjEqual(newDateRange, dateRange)) {
        setDateRange(newDateRange)
      }
    }
  }

  const onSelectEvent = ({ id }) => {
    // Check if the event is a calendar event
    const calendarEvent = calendarEvents.find(event => event.id === id)
    if (calendarEvent) {
      dispatch(ActionCreators.showCalendarEventDetailsScreen(id))
      return
    }

    // Handle alarm events
    const alarm = Utils.getObjectWithId(allAlarms, id)

    if (!alarm) return

    const isParticipantAlarm =
      alarm.alarmCategory === Constants.AlarmCategories.PARTICIPANT_ALARM

    if (isParticipantAlarm) {
      dispatch(ActionCreators.showParticipantAlarmDetailsScreen(id))
    } else {
      dispatch(ActionCreators.showAlarmDetailsScreen(id))
    }
  }

  // eslint-disable-next-line no-unused-vars
  const eventTimeRangeFormat = ({ start, end }) => {
    // Returning undefined here such that the event start time and end time including the : is also not shown
    return undefined
  }

  const eventPropGetter = event => {
    const categoryColor = AlarmUtils.getColorForAlarmCategory(
      colorScheme,
      event.category.color
    )

    const theme = colorThemes.getColorTheme(colorScheme)
    const isEventOnPastDate = DateTimeUtils.isDateBeforeToday(event.start)
    const textColor = isEventOnPastDate ? theme.darkTint : theme.textColor
    const isCategorized =
      event.category.id !== Constants.UNCATEGORIZED_ALARM_CATEGORY_ID

    return {
      style: {
        backgroundColor: theme.textBackgroundColor,
        border: 'none',
        ...(isCategorized
          ? {
              borderLeft: `6px solid ${categoryColor}`,
              padding: '2px 2px 2px 8px'
            }
          : { padding: '2px 2px 2px 6px' }),
        color: textColor,
        minHeight: '24px',
        fontSize: 16,
        ...(calendarView === 'week'
          ? {
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis'
            }
          : {})
      }
    }
  }

  const showMoreComponent = useCallback(
    ({ count, slotDate }) => {
      const theme = colorThemes.getColorTheme(colorScheme)
      const textColor = DateTimeUtils.isDateBeforeToday(slotDate)
        ? theme.darkTint
        : theme.textColor
      return (
        <button
          type="button"
          onMouseDown={e => e.stopPropagation()}
          onMouseUp={e => e.stopPropagation()}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            onNavigate(slotDate)
            onChangeCalendarView('day')
          }}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: textColor,
            cursor: 'pointer',
            fontSize: 14,
            paddingLeft: 6
          }}>
          {I18n.t('nMore', { count })}
        </button>
      )
    },
    [colorScheme, onNavigate, onChangeCalendarView]
  )

  const dayPropGetter = day => {
    if (DateTimeUtils.isDateToday(day)) {
      return {
        style: {
          backgroundColor: colorThemes.getColorTheme(colorScheme).tileHeader
        }
      }
    } else if (DateTimeUtils.isDateBeforeToday(day)) {
      return {
        style: {
          backgroundColor: colorThemes.getColorTheme(colorScheme).lightTint
        }
      }
    } else if (
      calendarView === 'month' &&
      moment(day).month() !== monthForCalendarView
    ) {
      return {
        style: {
          backgroundColor: colorThemes.getColorTheme(colorScheme).lightTint
        }
      }
    }
    return {
      style: {
        backgroundColor: colorThemes.getColorTheme(colorScheme).lightTint
      }
    }
  }

  // This applies each half hour slot in week and day view
  // eslint-disable-next-line no-unused-vars
  const slotPropGetter = (...props) => {
    return {
      style: {
        borderColor: 'transparent'
      }
    }
  }

  return (
    <Box flex background="screenBackgroundColor">
      <Calendar
        views={['month', 'week', 'day']} // Remove agenda
        view={calendarView}
        date={calendarDate}
        localizer={localizer}
        events={events}
        style={{
          backgroundColor:
            colorThemes.getColorTheme(colorScheme).screenBackgroundColor
        }}
        selectable // Needed to make the cells clickable
        onView={onChangeCalendarView}
        onNavigate={onNavigate}
        onRangeChange={onRangeChange}
        onSelectSlot={onSelectSlot}
        onSelectEvent={onSelectEvent}
        components={{
          toolbar: CalendarToolbar,
          showMore: showMoreComponent
        }}
        formats={{ eventTimeRangeFormat: eventTimeRangeFormat }}
        eventPropGetter={eventPropGetter}
        dayPropGetter={dayPropGetter}
        slotPropGetter={slotPropGetter}
        scrollToTime={new Date()}
        tooltipAccessor="tooltip"
      />
    </Box>
  )
}

export default CalendarView
